import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  IconButton,
} from '@mui/material';
import {
  useGetCampaignsQuery,
  useGetActiveCampaignQuery,
  useStartCampaignMutation,
  usePauseCampaignMutation,
  useResumeCampaignMutation,
  useStopCampaignMutation,
  Campaign,
} from '../store/api/outboundCallingApi';
import { ArrowBack, NavigateNext } from '@mui/icons-material';
import { useGetAgentsForAccountQuery } from '../store/api/synthflowAgentsApi';
import { useGetActiveSynthflowAccountsQuery } from '../store/api/synthflowApi';
import CampaignList from '../components/outbound-calling/CampaignList';
import CampaignStatus from '../components/outbound-calling/CampaignStatus';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`outbound-tabpanel-${index}`}
      aria-labelledby={`outbound-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const OutboundCallingPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loadingCampaignId, setLoadingCampaignId] = useState<number | null>(null);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState<number | null>(null);

  // Get agent_id from URL parameters
  const agentIdFromUrl = searchParams.get('agent_id');
  const accountIdFromUrl = searchParams.get('account_id');
  
  // Parse agent_id from URL for API query
  const agentIdForQuery = React.useMemo(() => {
    if (agentIdFromUrl) {
      const parsed = parseInt(agentIdFromUrl);
      console.log('🔍 Using agent_id from URL for API query:', parsed);
      return parsed;
    }
    return selectedAgent || undefined;
  }, [agentIdFromUrl, selectedAgent]);

  // Parse account_id from URL for API query
  const accountIdForQuery = React.useMemo(() => {
    if (accountIdFromUrl) {
      const parsed = parseInt(accountIdFromUrl);
      console.log('🔍 Using account_id from URL for API query:', parsed);
      return parsed;
    }
    return selectedAccount || undefined;
  }, [accountIdFromUrl, selectedAccount]);

  // Set selectedAgent from URL parameters
  React.useEffect(() => {
    if (agentIdFromUrl && !selectedAgent) {
      const parsedAgentId = parseInt(agentIdFromUrl);
      console.log('🔍 Setting selectedAgent from URL:', parsedAgentId);
      setSelectedAgent(parsedAgentId);
    }
  }, [agentIdFromUrl, selectedAgent]);

  // API queries - pass agent_id and account_id to backend for server-side filtering
  // Note: NOT passing status parameter, so ALL statuses (active, paused, stopped, completed) will be returned
  const { data: allCampaigns, isLoading: campaignsLoading, error: campaignsError, refetch: refetchCampaigns } = useGetCampaignsQuery(
    { agentId: agentIdForQuery, accountId: accountIdForQuery }, 
    {
      // Force refetch on mount and when data might be stale
      refetchOnMountOrArgChange: true,
    }
  );

  // Use campaigns directly from API (already filtered by backend)
  // All campaigns regardless of status are included since no status filter is applied
  const campaigns = React.useMemo(() => {
    if (campaignsError) {
      console.error('❌ Error fetching campaigns:', campaignsError);
      setError(`Failed to load campaigns: ${String(campaignsError)}`);
    }
    if (!allCampaigns) {
      console.log('📭 No campaigns data from API');
      if (!campaignsLoading) {
        console.log('⚠️ Campaigns query completed but returned no data');
      }
      return [];
    }
    console.log(`📊 Found ${allCampaigns.length} campaigns from API (agent_id=${agentIdForQuery}, account_id=${accountIdForQuery})`);
    if (allCampaigns.length > 0) {
      // Log status breakdown
      const statusCounts: Record<string, number> = {};
      allCampaigns.forEach(c => {
        statusCounts[c.status] = (statusCounts[c.status] || 0) + 1;
      });
      console.log('📊 Campaign status breakdown:', statusCounts);
      console.log('📋 Campaign details:', allCampaigns.map(c => ({ 
        id: c.id, 
        status: c.status,
        agent_id: c.agent_id, 
        umbrella_account_id: c.umbrella_account_id, 
        name: c.name 
      })));
    } else if (!campaignsLoading && agentIdForQuery) {
      console.warn(`⚠️ No campaigns found for agent_id=${agentIdForQuery}, account_id=${accountIdForQuery}`);
    }
    return allCampaigns;
  }, [allCampaigns, agentIdForQuery, accountIdForQuery, campaignsLoading, campaignsError]);
  
  // Get accounts to find which account this agent belongs to
  const { data: accounts = [] } = useGetActiveSynthflowAccountsQuery();
  
  // Set selectedAccount from URL parameters
  React.useEffect(() => {
    if (accountIdFromUrl) {
      setSelectedAccount(parseInt(accountIdFromUrl));
    } else if (selectedAgent && accounts.length > 0) {
      // For now, use the first account - in a real implementation, 
      // we'd need to determine which account the agent belongs to
      setSelectedAccount(accounts[0].id);
    }
  }, [selectedAgent, accounts, accountIdFromUrl]);
  
  const { data: agents, isLoading: agentsLoading } = useGetAgentsForAccountQuery(selectedAccount || 1, {
    skip: !selectedAccount
  });
  const { data: activeCampaign } = useGetActiveCampaignQuery(selectedAgent || 0, {
    skip: !selectedAgent,
  });

  // Mutations
  const [startCampaign] = useStartCampaignMutation();
  const [pauseCampaign] = usePauseCampaignMutation();
  const [resumeCampaign] = useResumeCampaignMutation();
  const [stopCampaign] = useStopCampaignMutation();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAgentSelect = (agentId: number) => {
    setSelectedAgent(agentId);
  };

  const handleViewDetails = (campaignId: number) => {
    navigate(`/outbound-calling/campaign/${campaignId}`);
  };

  const handleCampaignAction = async (campaignId: number, action: string) => {
    try {
      console.log(`Attempting ${action} for campaign ${campaignId}`);
      setError(null);
      setSuccess(null);
      setLoadingCampaignId(campaignId);
      
      switch (action) {
        case 'start':
          console.log('Starting campaign...');
          const result = await startCampaign(campaignId).unwrap();
          console.log('Start campaign result:', result);
          setSuccess('Campaign started successfully!');
          break;
        case 'pause':
          console.log('Pausing campaign...');
          await pauseCampaign(campaignId).unwrap();
          setSuccess('Campaign paused successfully!');
          break;
        case 'resume':
          console.log('Resuming campaign...');
          await resumeCampaign(campaignId).unwrap();
          setSuccess('Campaign resumed successfully!');
          break;
        case 'stop':
          console.log('Stopping campaign...');
          await stopCampaign(campaignId).unwrap();
          setSuccess('Campaign stopped successfully!');
          break;
      }
      console.log(`${action} completed successfully`);
      
      // Small delay to ensure backend has updated
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Manually refetch campaigns to ensure UI updates
      console.log('Refetching campaigns...');
      try {
        // Force a complete refetch with cache busting
        await refetchCampaigns();
        console.log('Campaigns refetched successfully');
        
        // Force a component refresh
        setForceRefresh(prev => prev + 1);
        console.log('Force refresh triggered');
        
        // Additional delay and another refetch to ensure data is fresh
        await new Promise(resolve => setTimeout(resolve, 1000));
        await refetchCampaigns();
        console.log('Second refetch completed');
        
        // Another force refresh
        setForceRefresh(prev => prev + 1);
        console.log('Second force refresh triggered');
        
        // Direct API call to verify campaign status
        try {
          const response = await fetch(`/api/v1/outbound-calling/campaigns/${campaignId}/status`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
          });
          const campaignStatus = await response.json();
          console.log('Direct API status call result:', campaignStatus);
          console.log('Campaign status from API:', campaignStatus.campaign?.status);
          console.log('Full campaign object:', campaignStatus.campaign);
        } catch (directApiError) {
          console.error('Direct API status call failed:', directApiError);
        }
      } catch (refetchError) {
        console.error('Error refetching campaigns:', refetchError);
      }
      
      // Clear success message after a delay
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error(`Error during ${action}:`, err);
      console.error('Full error object:', err);
      
      // Extract detailed error message
      let errorMessage = 'An error occurred';
      if (err.data?.detail) {
        errorMessage = err.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.status) {
        errorMessage = `Request failed with status ${err.status}`;
      }
      
      setError(`Failed to ${action} campaign: ${errorMessage}`);
    } finally {
      setLoadingCampaignId(null);
    }
  };


  if (campaignsLoading || agentsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (campaignsError) {
    // Extract error message for better debugging
    let errorMessage = 'Failed to load campaigns. Please try again.';
    if ('data' in campaignsError) {
      const errorData = campaignsError.data as any;
      if (errorData?.detail) {
        errorMessage = `Failed to load campaigns: ${errorData.detail}`;
      } else if (errorData?.message) {
        errorMessage = `Failed to load campaigns: ${errorData.message}`;
      }
    } else if ('status' in campaignsError) {
      errorMessage = `Failed to load campaigns: Request failed with status ${campaignsError.status}`;
    } else if ('message' in campaignsError) {
      errorMessage = `Failed to load campaigns: ${campaignsError.message}`;
    }
    
    console.error('Campaigns loading error:', campaignsError);
    
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => {
            refetchCampaigns();
          }}
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Navigation Breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={() => navigate('/aigent/agents')} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Breadcrumbs aria-label="breadcrumb" separator={<NavigateNext fontSize="small" />}>
            <Link color="inherit" onClick={() => navigate('/aigent/agents')} sx={{ cursor: 'pointer' }}>
              {accounts.find((acc: any) => acc.id === selectedAccount)?.name || 'Account'}
            </Link>
            {selectedAgent && agents && (
              <Link 
                color="inherit" 
                onClick={() => navigate('/aigent/agents')} 
                sx={{ cursor: 'pointer' }}
              >
                {agents.find((agent: any) => agent.id === selectedAgent)?.name || `Agent ${selectedAgent}`}
              </Link>
            )}
            <Typography color="text.primary">
              Campaigns
            </Typography>
          </Breadcrumbs>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="outbound calling tabs">
          <Tab label="Campaigns" />
          <Tab label="Campaign Status" />
        </Tabs>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Outbound Calling Campaigns</Typography>
          <Button
            variant="contained"
            onClick={() => {
              const agentId = searchParams.get('agent_id');
              const accountId = searchParams.get('account_id');
              const params = new URLSearchParams();
              if (agentId) params.set('agent_id', agentId);
              if (accountId) params.set('account_id', accountId);
              navigate(`/outbound-calling/create?${params.toString()}`);
            }}
            sx={{ ml: 2 }}
          >
            Create New Campaign
          </Button>
        </Box>

        {campaigns && campaigns.length > 0 ? (
          <CampaignList
            campaigns={campaigns}
            onCampaignAction={handleCampaignAction}
            onViewDetails={handleViewDetails}
            loadingCampaignId={loadingCampaignId}
          />
        ) : (
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" align="center">
                No campaigns found. Create your first campaign to get started.
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="h4" gutterBottom>
          Campaign Status
        </Typography>
        
        {agents && agents.length > 0 ? (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {agents.map((agent: any) => (
              <Grid item xs={12} sm={6} md={4} key={agent.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: selectedAgent === agent.id ? 2 : 1,
                    borderColor: selectedAgent === agent.id ? 'primary.main' : 'divider',
                  }}
                  onClick={() => handleAgentSelect(agent.id)}
                >
                  <CardContent>
                    <Typography variant="h6">{agent.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {agent.description}
                    </Typography>
                    {selectedAgent === agent.id && activeCampaign && (
                      <Chip
                        label={activeCampaign.status}
                        color={activeCampaign.status === 'active' ? 'success' : 'warning'}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="info">
            No agents found. Please create an agent first.
          </Alert>
        )}

        {selectedAgent && activeCampaign && (
          <CampaignStatus campaign={activeCampaign} />
        )}

        {selectedAgent && !activeCampaign && (
          <Alert severity="info">
            No active campaign found for the selected agent.
          </Alert>
        )}
      </TabPanel>


    </Container>
  );
};

export default OutboundCallingPage;
